import React, { useEffect, useState } from "react";
import { ListChecks, Plus, Copy, Trash2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const PRIOS = [
  { v: "baixa", l: "Baixa", c: "bg-slate-100 text-slate-600 border-slate-300" },
  { v: "normal", l: "Normal", c: "bg-sky-50 text-sky-700 border-sky-300" },
  { v: "alta", l: "Alta", c: "bg-rose-50 text-rose-700 border-rose-300" },
];
const prioOf = (v) => PRIOS.find((p) => p.v === v) || PRIOS[1];
const EMPTY = { text: "", notes: "", priority: "normal" };

function PrioPicker({ value, onChange, testid }) {
  return (
    <div className="flex gap-1.5">
      {PRIOS.map((p) => (
        <button key={p.v} type="button" onClick={() => onChange(p.v)} data-testid={`${testid}-${p.v}`}
          className={`flex-1 rounded-md border-2 py-1 text-[11px] font-bold transition-colors ${value === p.v ? p.c : "border-slate-200 text-slate-500 bg-white"}`}>
          {p.l}
        </button>
      ))}
    </div>
  );
}

function IdeaForm({ initial, onSave, onCancel, testid }) {
  const [f, setF] = useState(initial);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  return (
    <div className="space-y-2 rounded-lg border border-violet-200 bg-violet-50/50 p-2.5 min-w-0" data-testid={testid}>
      <Textarea value={f.text} onChange={(e) => set("text")(e.target.value)} placeholder="O que fazer / ideia..." rows={2} data-testid={`${testid}-text`} />
      <Textarea value={f.notes} onChange={(e) => set("notes")(e.target.value)} placeholder="Observações / detalhes (opcional)" rows={2} data-testid={`${testid}-notes`} />
      <PrioPicker value={f.priority} onChange={set("priority")} testid={`${testid}-prio`} />
      <div className="flex gap-2 justify-end">
        {onCancel && <Button size="sm" variant="ghost" onClick={onCancel} data-testid={`${testid}-cancel`}><X className="w-4 h-4" /></Button>}
        <Button size="sm" disabled={!f.text.trim()} onClick={() => onSave(f)} className="bg-violet-600 hover:bg-violet-700" data-testid={`${testid}-save`}>
          <Check className="w-4 h-4 mr-1" /> Salvar
        </Button>
      </div>
    </div>
  );
}

function IdeaRow({ it, onPatch, onDelete }) {
  const [editing, setEditing] = useState(false);
  const p = prioOf(it.priority);
  const copy = async () => {
    const txt = it.notes ? `${it.text}\n\n${it.notes}` : it.text;
    try { await navigator.clipboard.writeText(txt); toast.success("Texto copiado"); } catch { toast.error("Não foi possível copiar"); }
  };
  if (editing) {
    return <IdeaForm initial={{ text: it.text, notes: it.notes || "", priority: it.priority }} testid={`idea-edit-${it.id}`}
      onCancel={() => setEditing(false)} onSave={async (f) => { await onPatch(it.id, f); setEditing(false); }} />;
  }
  return (
    <li className={`rounded-lg border p-2.5 flex items-start gap-2 min-w-0 ${it.done ? "bg-slate-50 border-slate-200" : "bg-white border-slate-200"}`} data-testid={`idea-row-${it.id}`}>
      <input type="checkbox" checked={it.done} onChange={() => onPatch(it.id, { done: !it.done })} className="mt-1 w-4 h-4 accent-violet-600 shrink-0" data-testid={`idea-done-${it.id}`} />
      <div className="min-w-0 flex-1">
        <p className={`text-[13px] font-semibold break-words ${it.done ? "line-through text-slate-400" : "text-slate-800"}`} data-testid={`idea-text-${it.id}`}>{it.text}</p>
        {it.notes && <p className="text-[11.5px] text-slate-500 mt-0.5 whitespace-pre-wrap break-words">{it.notes}</p>}
        <span className={`inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded border ${p.c}`}>{p.l}</span>
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <button type="button" onClick={copy} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`idea-copy-${it.id}`}><Copy className="w-4 h-4" /></button>
        <button type="button" onClick={() => setEditing(true)} className="p-1.5 rounded hover:bg-slate-100 text-violet-600" data-testid={`idea-edit-btn-${it.id}`}><Pencil className="w-4 h-4" /></button>
        <button type="button" onClick={() => onDelete(it.id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" data-testid={`idea-delete-${it.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    </li>
  );
}

export default function IdeasChecklistDialog({ open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [tab, setTab] = useState("pending");

  useEffect(() => {
    if (open) { api.get("/ideas").then((r) => setItems(r.data)).catch(() => toast.error("Erro ao carregar")); }
  }, [open]);

  const create = async (f) => {
    const r = await api.post("/ideas", f);
    setItems((s) => [...s, r.data]); setAdding(false);
  };
  const patch = async (id, f) => {
    const r = await api.patch(`/ideas/${id}`, f);
    setItems((s) => s.map((x) => (x.id === id ? r.data : x)));
    if (f.done !== undefined) toast.success(f.done ? "Movido para Concluídos" : "Voltou para Pendentes");
  };
  const remove = async (id) => {
    if (!window.confirm("Excluir este item?")) return;
    await api.delete(`/ideas/${id}`);
    setItems((s) => s.filter((x) => x.id !== id));
  };
  const rank = { alta: 0, normal: 1, baixa: 2 };
  const sorted = [...items].filter((x) => (tab === "done" ? x.done : !x.done)).sort((a, b) => (tab === "done" ? (b.updated_at || "").localeCompare(a.updated_at || "") : rank[a.priority] - rank[b.priority]));
  const pending = items.filter((x) => !x.done).length;
  const doneCount = items.length - pending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden w-[calc(100vw-1.5rem)] sm:w-full grid-cols-1 [&>*]:min-w-0" data-testid="ideas-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ListChecks className="w-5 h-5 text-violet-600" /> Ideias e melhorias</DialogTitle>
          <DialogDescription className="text-[11.5px]" data-testid="ideas-pending-count">{pending} pendente(s) de {items.length}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1" data-testid="ideas-tabs">
          {[["pending", `Pendentes (${pending})`], ["done", `Concluídos (${doneCount})`]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`ideas-tab-${k}`}
              className={`h-8 rounded-md text-xs font-bold transition-colors ${tab === k ? "bg-white text-violet-700 shadow" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
        {tab === "pending" && (adding ? <IdeaForm initial={EMPTY} onSave={create} onCancel={() => setAdding(false)} testid="idea-new" />
          : <Button onClick={() => setAdding(true)} variant="outline" className="border-dashed border-violet-300 text-violet-700" data-testid="idea-add-btn"><Plus className="w-4 h-4 mr-1" /> Novo item</Button>)}
        {sorted.length === 0 && <p className="text-center text-xs text-slate-400 py-4" data-testid="ideas-empty">{tab === "done" ? "Nada concluído ainda." : "Nenhuma ideia pendente."}</p>}
        <ul className="space-y-2" data-testid="ideas-list">
          {sorted.map((it) => <IdeaRow key={it.id} it={it} onPatch={patch} onDelete={remove} />)}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
